import type { RuntimeConfig } from '../config';
import type { Credentials, Lobby, LobbyResponse } from './protocol';
import { physicsQuery } from './physicsRevision';

const requestTimeoutMs = 8_000;

export interface RealtimeGrant {
  grant: string;
  expiresAt: string;
}

export class APIError extends Error {
  public constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

export class LobbyService {
  public constructor(
    private readonly config: RuntimeConfig,
    private readonly authorization: () => string | null = () => null,
  ) {}

  public create(displayName: string): Promise<LobbyResponse> {
    return this.request<LobbyResponse>('/api/v1/lobbies', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  }

  public quickplay(displayName: string): Promise<LobbyResponse> {
    return this.request<LobbyResponse>('/api/v1/matchmaking/quickplay', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  }

  public training(displayName: string): Promise<LobbyResponse> {
    return this.request<LobbyResponse>('/api/v1/matchmaking/training', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  }

  public join(code: string, displayName: string): Promise<LobbyResponse> {
    return this.request<LobbyResponse>('/api/v1/lobbies/' + encodeURIComponent(code) + '/join', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  }

  public lobby(code: string): Promise<Lobby> {
    return this.request<Lobby>('/api/v1/lobbies/' + encodeURIComponent(code));
  }

  public leave(credentials: Credentials): Promise<void> {
    return this.request<void>('/api/v1/lobbies/' + encodeURIComponent(credentials.lobbyCode) + '/membership', {
      method: 'DELETE',
      body: JSON.stringify({ playerId: credentials.playerId, ticket: credentials.ticket }),
    });
  }

  public realtimeGrant(credentials: Credentials): Promise<RealtimeGrant> {
    return this.request<RealtimeGrant>('/api/v1/lobbies/' + encodeURIComponent(credentials.lobbyCode) + `/realtime-grant?${physicsQuery}`, {
      method: 'POST',
      body: JSON.stringify({ playerId: credentials.playerId, ticket: credentials.ticket }),
    });
  }

  public remember(credentials: Credentials, ownerID: string): void {
    sessionStorage.setItem(ticketKey(credentials.lobbyCode), JSON.stringify({ ...credentials, ownerID }));
  }

  public recall(code: string, ownerID: string): Credentials | null {
    const raw = sessionStorage.getItem(ticketKey(code));
    if (!raw) return null;
    try {
      const value = JSON.parse(raw) as Partial<Credentials> & { ownerID?: string };
      if (value.ownerID !== ownerID) {
        this.forget(code);
        return null;
      }
      if (value.lobbyCode === code && typeof value.playerId === 'string' && typeof value.ticket === 'string') {
        return { lobbyCode: value.lobbyCode, playerId: value.playerId, ticket: value.ticket };
      }
      this.forget(code);
      return null;
    } catch {
      this.forget(code);
      return null;
    }
  }

  public forget(code: string): void {
    sessionStorage.removeItem(ticketKey(code));
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), requestTimeoutMs);
    const authorization = this.authorization();
    try {
      const response = await fetch(this.config.apiOrigin + path, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(authorization ? { Authorization: authorization } : {}),
          ...init.headers,
        },
        signal: controller.signal,
      });
      if (response.status === 204) return undefined as T;
      let payload: T & { error?: { code?: string; message?: string } };
      try {
        payload = await response.json() as T & { error?: { code?: string; message?: string } };
      } catch {
        if (!response.ok) {
          throw new APIError(
            response.status === 404 ? 'That yard route is not available.' : 'The yard signal failed. Try again.',
            response.status === 404 ? 'not_found' : 'request_failed',
            response.status,
          );
        }
        throw new APIError('The yard sent an unreadable response.', 'invalid_response', response.status);
      }
      if (!response.ok) {
        throw new APIError(
          payload.error?.message ?? 'The yard signal failed. Try again.',
          payload.error?.code ?? 'request_failed',
          response.status,
        );
      }
      return payload;
    } catch (error) {
      if (error instanceof APIError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new APIError('The yard did not answer in time.', 'timeout', 0);
      }
      throw new APIError('Cannot reach the SCRAPLINE server.', 'offline', 0);
    } finally {
      window.clearTimeout(timer);
    }
  }
}

function ticketKey(code: string): string {
  return 'scrapline:ticket:' + code;
}
